/*!
   Copyright 2019 Ron Buckton

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

       http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.
*/
import { R } from 'retemplate';
import { normalizeString, ZONEDDATETIME_HASH_SEED } from './internal/core';
import { getDate, getDatePart, getCalendarDateFromOrdinalDate, getCalendarDateFromWeekDate, DatePart } from './internal/date';
import { getTime, getTimePart, TimePart } from './internal/time';
import { getInstant, getDateOfInstant, getTimeOfInstant, adjustInstant } from './internal/instant';
import { INSTANT, ZONE } from './internal/symbols';
import { ZONED_DATETIME_EXTENDED, ZONED_DATETIME_BASIC } from './internal/patterns';
import { Temporal, Components, DateDelta, TimeDelta, DateUnit, TimeUnit } from './types';
import { Zone } from './Zone';
import { Clock } from './Clock';
import { CivilDate } from './CivilDate';
import { CivilTime } from './CivilTime';
import { CivilDateTime } from './CivilDateTime';
import { OffsetTime } from './OffsetTime';
import { OffsetDateTime } from './OffsetDateTime';
import { Instant } from './Instant';
import { Equatable, Comparable } from '@esfx/equatable';

const zonedDateTimeExtendedRegExp = new RegExp(R`^${ZONED_DATETIME_EXTENDED}$`);
const zonedDateTimeBasicRegExp = new RegExp(R`^${ZONED_DATETIME_BASIC}$`);

export class ZonedDateTime implements Temporal, Equatable, Comparable {
    /* @internal */ private [INSTANT]: Instant;
    /* @internal */ private [ZONE]: Zone;

    constructor(instant: Instant, zone: Zone | string) {
        if (typeof zone === "string") zone = Zone.fromString(zone);
        this[INSTANT] = instant;
        this[ZONE] = zone;
    }

    get instant() { return this[INSTANT]; }
    get zone() { return this[ZONE]; }
    get ianaZone() { return this.zone.ianaZone; }
    get timeZone() { return this.zone.ianaZone || this.offsetString; }
    get offset() { return this.zone.getOffset(this.instant); }
    get offsetSeconds() { return this.zone.getOffsetSeconds(this.instant); }
    get offsetString() { return this.zone.getOffsetString(this.instant); }
    get year() { return getDatePart(toDate(this.instant, this.offsetSeconds), DatePart.year); }
    get month() { return getDatePart(toDate(this.instant, this.offsetSeconds), DatePart.month); }
    get day() { return getDatePart(toDate(this.instant, this.offsetSeconds), DatePart.day); }
    get dayOfYear() { return getDatePart(toDate(this.instant, this.offsetSeconds), DatePart.dayOfYear); }
    get dayOfWeek() { return getDatePart(toDate(this.instant, this.offsetSeconds), DatePart.dayOfWeek); }
    get weekOfYear() { return getDatePart(toDate(this.instant, this.offsetSeconds), DatePart.weekOfYear); }
    get hour() { return getTimePart(toTime(this.instant, this.offsetSeconds), TimePart.hour); }
    get minute() { return getTimePart(toTime(this.instant, this.offsetSeconds), TimePart.minute); }
    get second() { return getTimePart(toTime(this.instant, this.offsetSeconds), TimePart.second); }
    get millisecond() { return getTimePart(toTime(this.instant, this.offsetSeconds), TimePart.millisecond); }
    get nanosecond() { return getTimePart(toTime(this.instant, this.offsetSeconds), TimePart.nanosecond); }

    plus(deltas?: Components<DateDelta | TimeDelta>) {
        return this
            .toCivilDateTime()
            .plus(deltas)
            .withZone(this.zone);
    }

    minus(deltas?: Components<DateDelta | TimeDelta>) {
        return this
            .toCivilDateTime()
            .minus(deltas)
            .withZone(this.zone);
    }

    with(components?: Components<DateUnit | TimeUnit>) {
        return this
            .toCivilDateTime()
            .with(components)
            .withZone(this.zone);
    }

    /**
     * Returns a new [[ZonedDateTime]] with the same [[Instant]], but for a different time-zone.
     * @param zone The new time zone.
     * @example
     * const a = ZonedDateTime.fromString("2019-01-04T15:38:00.000000000-08:00[America/Los_Angeles]");
     * const b = a.withZoneSameInstant("America/New_York");
     * console.log(b);
     *
     * // prints:
     * // 2019-01-04T18:38:00.000000000-05:00[America/New_York]
     */
    withZoneSameInstant(zone: Zone | string) {
        if (typeof zone === "string") zone = Zone.fromString(zone);
        if (this.zone.equals(zone)) return this;
        return new ZonedDateTime(this.instant, zone);
    }

    /**
     * Returns a new [[ZonedDateTime]] with the same local time, but for a different time-zone.
     * @param zone The new time zone.
     * @example
     * const a = ZonedDateTime.fromString("2019-01-04T15:38:00.000000000-08:00[America/Los_Angeles]");
     * const b = a.withZoneSameLocal("America/New_York");
     * console.log(b);
     *
     * // prints:
     * // 2019-01-04T15:38:00.000000000-05:00[America/New_York]
     */
    withZoneSameLocal(zone: Zone | string) {
        if (typeof zone === "string") zone = Zone.fromString(zone);
        if (this.zone.equals(zone)) return this;

        const tz1 = zone.getOffsetSeconds(this.instant);
        let instant = this.instant.plus({ seconds: this.offsetSeconds - tz1 });

        // check for possible DST adjustment
        const tz2 = zone.getOffsetSeconds(instant);
        if (tz2 !== tz1) {
            instant = instant.plus({ seconds: tz1 - tz2 });
        }

        return new ZonedDateTime(instant, zone);
    }

    until(other: ZonedDateTime) {
        if (!(other instanceof ZonedDateTime)) throw new TypeError("ZonedDateTime expected: other");
        return this
            .toCivilDateTime()
            .until(other
                .withZoneSameInstant(this.zone)
                .toCivilDateTime());
    }

    since(other: ZonedDateTime) {
        if (!(other instanceof ZonedDateTime)) throw new TypeError("ZonedDateTime expected: other");
        return other.until(this);
    }

    equals(other: ZonedDateTime) {
        return other !== null
            && other !== undefined
            && this.instant.equals(other.instant)
            && this.zone.equals(other.zone);
    }

    compareTo(other: ZonedDateTime): number {
        return this.instant.compareTo(other.instant);
    }

    toCivilDate() {
        return new CivilDate(
            this.year,
            this.month,
            this.day);
    }

    toCivilTime() {
        return new CivilTime(
            this.hour,
            this.minute,
            this.second,
            this.millisecond,
            this.nanosecond);
    }

    toCivilDateTime() {
        return new CivilDateTime(
            this.year,
            this.month,
            this.day,
            this.hour,
            this.minute,
            this.second,
            this.millisecond,
            this.nanosecond);
    }

    toOffsetTime() {
        return new OffsetTime(
            this.zone.getOffset(this.instant),
            this.hour,
            this.minute,
            this.second,
            this.millisecond,
            this.nanosecond);
    }

    toOffsetDateTime() {
        return new OffsetDateTime(
            this.zone.getOffset(this.instant),
            this.year,
            this.month,
            this.day,
            this.hour,
            this.minute,
            this.second,
            this.millisecond,
            this.nanosecond);
    }

    toZonedCalendarDateTimeString() {
        return `${this.toCivilDateTime().toCalendarDateTimeString()}${this.zone.getZoneString(this.instant)}`;
    }

    toZonedWeekDateTimeString() {
        return `${this.toCivilDateTime().toWeekDateTimeString()}${this.zone.getZoneString(this.instant)}`;
    }

    toZonedOrdinalDateTimeString() {
        return `${this.toCivilDateTime().toOrdinalDateTimeString()}${this.zone.getZoneString(this.instant)}`;
    }

    toISOCalendarDateTimeString() {
        return `${this.toCivilDateTime().toCalendarDateTimeString()}${this.zone.getOffsetString(this.instant)}`;
    }

    toISOWeekDateTimeString() {
        return `${this.toCivilDateTime().toWeekDateTimeString()}${this.zone.getOffsetString(this.instant)}`;
    }

    toISOOrdinalDateTimeString() {
        return `${this.toCivilDateTime().toOrdinalDateTimeString()}${this.zone.getOffsetString(this.instant)}`;
    }

    toString() {
        return this.toZonedCalendarDateTimeString();
    }

    toISOString() {
        return this.toISOCalendarDateTimeString();
    }

    toJSON() {
        return this.toZonedCalendarDateTimeString();
    }

    static fromCalendarDateTime(zone: Zone | string, year: number, month: number, day: number, hour: number, minute: number, second = 0, millisecond = 0, nanosecond = 0) {
        const ns = getInstant(getDate(year, month, day), getTime(hour, minute, second, millisecond, nanosecond));
        return new Instant(ns)
            .withZone(Zone.UTC)
            .withZoneSameLocal(zone);
    }

    static fromWeekDateTime(zone: Zone | string, year: number, weekOfYear: number, dayOfWeek: number, hour: number, minute: number, second = 0, millisecond = 0, nanosecond = 0) {
        const calendar = getCalendarDateFromWeekDate(year, weekOfYear, dayOfWeek);
        return ZonedDateTime.fromCalendarDateTime(
            zone,
            calendar.year,
            calendar.month,
            calendar.day,
            hour,
            minute,
            second,
            millisecond,
            nanosecond);

    }

    static fromOrdinalDateTime(zone: Zone | string, year: number, dayOfYear: number, hour: number, minute: number, second = 0, millisecond = 0, nanosecond = 0) {
        const calendar = getCalendarDateFromOrdinalDate(year, dayOfYear);
        return ZonedDateTime.fromCalendarDateTime(
            zone,
            calendar.year,
            calendar.month,
            calendar.day,
            hour,
            minute,
            second,
            millisecond,
            nanosecond);
    }

    static fromString(text: string) {
        const normalText = normalizeString(text);
        const match = zonedDateTimeExtendedRegExp.exec(normalText) || zonedDateTimeBasicRegExp.exec(normalText);
        if (match) {
            const { year, month, day, weekOfYear, dayOfWeek, dayOfYear, hour, minute, second, millisecond = "", nanosecond = "", offset, ianaZone } = match.groups!;
            const zone = Zone.fromString(ianaZone || offset);
            if (year && month && day) return ZonedDateTime.fromCalendarDateTime(zone, +year, +month, +day, +hour, +minute, +second, +millisecond.padEnd(3, "0"), +nanosecond.padEnd(6, "0"));
            if (year && weekOfYear && dayOfWeek) return ZonedDateTime.fromWeekDateTime(zone, +year, +weekOfYear, +dayOfWeek, +hour, +minute, +second, +millisecond.padEnd(3, "0"), +nanosecond.padEnd(6, "0"));
            if (year && dayOfYear) return ZonedDateTime.fromOrdinalDateTime(zone, +year, +dayOfYear, +hour, +minute, +second, +millisecond.padEnd(3, "0"), +nanosecond.padEnd(6, "0"));
        }

        throw new SyntaxError(`Invalid date-time string '${text}'`);
    }

    static now(clock: Clock) {
        return clock.currentInstant().withZone(clock.zone);
    }

    [Equatable.equals](other: unknown) {
        return other instanceof ZonedDateTime
            && this.equals(other);
    }

    [Equatable.hash]() {
        let hc = ZONEDDATETIME_HASH_SEED;
        hc = ((hc << 7) | (hc >>> 25)) ^ this[INSTANT][Equatable.hash]();
        hc = ((hc << 7) | (hc >>> 25)) ^ this[ZONE][Equatable.hash]();
        return hc;
    }

    [Comparable.compareTo](other: unknown) {
        if (!(other instanceof ZonedDateTime)) throw new TypeError();
        return this.compareTo(other);
    }
}

function toDate(instant: Instant, offsetSeconds: number) {
    return getDateOfInstant(toTicks(instant, offsetSeconds));
}

function toTime(instant: Instant, offsetSeconds: number) {
    return getTimeOfInstant(toTicks(instant, offsetSeconds));
}

function toTicks(instant: Instant, offsetSeconds: number) {
    return adjustInstant(getInstant(0n, getTime(0, 0, 0, instant.milliseconds, instant.nanoseconds)), offsetSeconds);
}

Object.defineProperty(ZonedDateTime.prototype, Symbol.toStringTag, {
    writable: true,
    configurable: true,
    value: "ZonedDateTime"
});